/* ============================================================
   "Copy a link to this run."

   share.js makes the string and states its four rules. This is the only
   place a user ever meets it, so it owns the part share.js explicitly
   left to the UI: the warning.

   That warning is the reason this is a component and not three lines
   inside the corridor run. A link to a run carries what the lane costs
   the operator and what they charge for it — their margin, reconstructable
   by anyone holding the URL. Nothing is transmitted to build one and the
   site still talks to no server, so there is no breach to report; the
   risk is entirely that a person pastes it somewhere they did not mean
   to. The privacy notice covers this, and nobody reads a privacy notice
   at the moment they press a button.

   So the warning is stated BEFORE the copy, in the button's own hint,
   and confirmed AFTER it in the toast. Not a modal: a confirmation
   dialog on a copy button is trained away in a week, and the second
   time someone sees it they are clicking Yes without reading. A sentence
   they cannot avoid seeing beats a dialog they have learned to dismiss.

   The fallback path matters more than it looks. navigator.clipboard is
   unavailable on insecure origins and older mobile browsers — exactly
   the operator on a shared depot laptop this product is built for. When
   it fails the URL is shown, selected, and left on screen to copy by
   hand, rather than a toast saying "could not copy" and nothing to
   copy from.
   ============================================================ */

import { shareUrl } from '../shared/share.js';
import { icon } from '../shared/icons.js';
import { toast } from '../shared/toast.js';

/**
 * @param {object} options
 * @param {string} options.path   route the link should open
 * @param {object} options.input  the run inputs to encode
 * @returns {HTMLElement}
 */
export function ShareButton({ path, input, label = 'Copy a link to this run' }) {
  const wrap = document.createElement('div');
  wrap.className = 'no-print';
  wrap.style.marginTop = 'var(--ca-space-3)';

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'btn btn-secondary btn-block';
  button.innerHTML = `${icon('link', 16)}<span>${label}</span>`;

  const hint = document.createElement('p');
  hint.style.cssText =
    'font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); text-align: center; margin-top: var(--ca-space-2);';
  hint.textContent =
    'The link carries the figures you entered — including your cost and your rate. Anyone who opens it sees them.';

  /* Shown only when the clipboard is unavailable. Built lazily so the
     URL is not sitting in the DOM of every result page unasked. */
  const fallback = document.createElement('div');
  fallback.hidden = true;
  fallback.style.marginTop = 'var(--ca-space-2)';

  button.addEventListener('click', async () => {
    const url = shareUrl(path, input);

    try {
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(url);
      toast('Link copied. It carries your cost and rate — check where you paste it.');
      return;
    } catch {
      // Not an error state: the link exists, this device just cannot put
      // it on the clipboard. Show it rather than report a failure.
      fallback.hidden = false;
      fallback.replaceChildren();

      const note = document.createElement('p');
      note.style.cssText =
        'font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-1);';
      note.textContent = 'This device would not let the page copy for you. Here is the link:';

      const field = document.createElement('input');
      field.type = 'text';
      field.readOnly = true;
      field.value = url;
      field.setAttribute('aria-label', 'Link to this run');
      field.style.width = '100%';
      field.addEventListener('focus', () => field.select());

      fallback.append(note, field);
      field.focus();
    }
  });

  wrap.append(button, hint, fallback);
  return wrap;
}
